import { apiFetch, apiFetchBlob } from '@/lib/api';

export type AttentionStatus = 'needs_attention' | 'resolved' | 'active' | 'idle';

export interface ConversationSummary {
  contact_id: number;
  ig_user_id: string;
  ig_username: string | null;
  lead_id: number | null;
  state: string | null;
  attention_status: AttentionStatus;
  bot_silenced: boolean;
  // F199 — end of the owner-takeover window (ISO), null when no window is open.
  owner_active_until: string | null;
  // F200 — the owner already knows this person (recent owner echo, won lead,
  // handoff); the bot runs in quiet mode. Non-nullable.
  known_contact: boolean;
  resolved_at: string | null;
  last_activity_at: string | null;
  last_message_snippet: string | null;
}

export interface TurnAudit {
  executed_tools: string[];
  guardrail_allowed: boolean | null;
  guardrail_rule: string | null;
  boundary_category: string | null;
  raw_text_empty: boolean;
}

export interface ThreadMessage {
  direction: 'inbound' | 'outbound' | 'owner';
  text: string | null;
  timestamp: string;
  inbound_message_id: number | null;
  outbound_message_id: number | null;
  audit: TurnAudit | null;
  // F165 — a NULL-text inbound (attachment-only DM the dispatcher
  // persisted-but-skipped); always false/null on an outbound message.
  unsupported: boolean;
  attachment_type: string | null;
  // F203 — shared-post caption/link; non-null only for ig_post|ig_reel|ig_story.
  attachment_title: string | null;
  attachment_url: string | null;
  // F202 — inbound status string; informational only, NEVER drive UI from it.
  status: string | null;
  // F202 — true only when the inbound was blocked by an inactive subscription
  // (not a quota cap-hit). The sole signal for the "subscrição inativa" caption.
  blocked_by_subscription: boolean;
}

export interface ConversationListResponse {
  items: ConversationSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface ConversationDetail {
  summary: ConversationSummary;
  messages: ThreadMessage[];
}

export interface ConversationListParams {
  status?: AttentionStatus;
  q?: string;
  page?: number;
  page_size?: number;
}

function buildQuery(params: ConversationListParams): string {
  const usp = new URLSearchParams();
  if (params.status) usp.append('status', params.status);
  if (params.q) usp.append('q', params.q);
  if (params.page !== undefined) usp.append('page', String(params.page));
  if (params.page_size !== undefined) usp.append('page_size', String(params.page_size));
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
}

// Module-local helper — shared by exportThread and any future export methods.
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const conversationService = {
  list(params: ConversationListParams = {}): Promise<ConversationListResponse> {
    return apiFetch<ConversationListResponse>(`/conversations${buildQuery(params)}`);
  },
  get(contactId: number): Promise<ConversationDetail> {
    return apiFetch<ConversationDetail>(`/conversations/${contactId}`);
  },
  assumeControl(contactId: number): Promise<ConversationSummary> {
    return apiFetch<ConversationSummary>(`/conversations/${contactId}/assume-control`, {
      method: 'POST',
    });
  },
  releaseControl(contactId: number): Promise<ConversationSummary> {
    return apiFetch<ConversationSummary>(`/conversations/${contactId}/release-control`, {
      method: 'POST',
    });
  },
  resolve(contactId: number): Promise<ConversationSummary> {
    return apiFetch<ConversationSummary>(`/conversations/${contactId}/resolve`, {
      method: 'POST',
    });
  },
  escalate(contactId: number): Promise<ConversationSummary> {
    return apiFetch<ConversationSummary>(`/conversations/${contactId}/escalate`, {
      method: 'POST',
    });
  },
  // F099/A11 — default export is the human-readable .txt. JSON stays available
  // via format='json' for support. The download filename comes from the
  // backend's Content-Disposition header (already format-aware).
  async exportThread(contactId: number, format: 'txt' | 'json' = 'txt'): Promise<void> {
    const query = format === 'json' ? '?format=json' : '';
    const { blob, filename } = await apiFetchBlob(`/conversations/${contactId}/export${query}`);
    triggerDownload(blob, filename);
  },
};
